import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { CardLayout, MTable } from "../v3";
import { PerformanceGuaranteesService, ProjectService } from "../../services";
import { PgTableColumns } from "../../pages/performanceGuarantees/constants";
import { EXPORT_PERFORMANCE_GUARANTEE } from "../../constants";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const PerformanceGuaranteesList = ({ projectId = null, reloadData, totalCount = 0, setTotalCount = () => {} }) => {
  const [performanceGuarantees, setPerformanceGuarantees] = useState([]);
  const [projects, setProjects] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "performance-guarantees",
    defaults: { sortModel: [{ field: "updatedAt", sort: "desc" }] },
  });
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const hasExportPermission = useHasPermission(EXPORT_PERFORMANCE_GUARANTEE);

  const fetchData = useCallback(
    async (filters = {}) => {
      setLoading(true);
      try {
        const { data: response } = await PerformanceGuaranteesService.getPerformanceGuarantees(filters);
        const projectIds = Array.from(new Set(response?.rows?.map((item) => item.projectId)));

        if (projectIds.length > 0) {
          const projectResponse = await ProjectService.getProjectList({
            ids: projectIds.join(","),
            limit: projectIds.length,
            includeDeleted: true,
          });
          setProjects(projectResponse?.data?.rows);
        }
        setPerformanceGuarantees(response?.rows);
        setTotalCount(response?.count);
      } catch (error) {
        console.error("Error fetching Performance Guarantees:", error);
      } finally {
        setLoading(false);
      }
    },
    [setTotalCount],
  );

  useEffect(() => {
    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      name: searchText,
      projectId,
    });
  }, [fetchData, sortModel, paginationState, searchText, projectId, reloadData]);

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing
          title="Performance Guarantees"
          columnHeaders={pdfColumnHeaders}
          tableRows={printableRows}
          company={user?.company}
        />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Performance Guarantees",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const onViewClick = (params, event) => {
    navigate(`/performance-guarantee/${params.row.id}`);
  };

  const columnHeaders = projectId ? PgTableColumns.filter((col) => col.field !== "projectName") : PgTableColumns;

  const rows = performanceGuarantees.map((pg, index) => {
    const project = projects.find((proj) => proj.id === pg.projectId);
    return {
      id: pg.id,
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      name: pg.name,
      projectName: project ? project.name : "N/A",
      description: pg.description,
      depositAmount: pg.depositAmount,
    };
  });

  return (
    <CardLayout>
      <MTable
        placeholder="Search..."
        searchText={searchText}
        onSearchChange={handleSearchChange}
        showSearchBar
        // Export data
        showExportButton={hasExportPermission}
        isPdfGenerating={isPdfGenerating}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={columnHeaders}
        tableRows={rows}
        rowCount={totalCount}
        loading={loading}
        sortModel={sortModel}
        onSortChange={setSortModel}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
        onRowClick={onViewClick}
      />
    </CardLayout>
  );
};
